import React, { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * DepartmentSelect (TO1a / R-TO4e)
 *
 * A picker over the ACTIVE departments table that writes the department NAME
 * into users.department. The column deliberately stays free text — NR1 routes
 * on it (R-NR3/R-NR4) — so this narrows what NEW writes can contain without
 * migrating existing values.
 *
 * The unlisted-value guard is the load-bearing part: if a user's stored
 * department is not in the list (an older free-text value such as
 * "Human Resources", or a department since deactivated), it is offered as a
 * selected "(unlisted)" option. Without it the select would render with no
 * match, report an empty value, and silently CLOBBER that user's department
 * the next time someone saved an unrelated field on the same form.
 */
const DepartmentSelect = ({ value, onChange, name = 'department', id }) => {
  const [departments, setDepartments] = useState([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/departments')
      .then(res => { if (!cancelled) setDepartments(res.data.departments || []); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  const current = (value || '').trim();
  const known = departments.some(d => d.name.trim().toLowerCase() === current.toLowerCase());

  // If the list could not be loaded, fall back to a free-text input rather than
  // presenting a picker that cannot offer the value the user already has.
  if (failed) {
    return (
      <input
        type="text"
        id={id}
        name={name}
        value={value || ''}
        onChange={onChange}
        placeholder="Department"
      />
    );
  }

  return (
    <select id={id} name={name} value={current} onChange={onChange}>
      <option value="">— none —</option>
      {current !== '' && !known && (
        <option value={current}>(unlisted) {current}</option>
      )}
      {departments.map(d => (
        <option key={d.id} value={d.name}>{d.name}</option>
      ))}
    </select>
  );
};

export default DepartmentSelect;
